import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Pencil, Check, X, History, UserCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--accent))]";

const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-PT") : "—");

const AdminsTab = () => {
  const [data, setData] = useState({ founders: [], entries: [] });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/access`, { withCredentials: true });
      setData(r.data);
    } catch { toast.error("Erro a carregar."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/access`, { email, role_label: role || null }, { withCredentials: true });
      toast.success(`Acesso concedido a ${email}.`);
      setEmail(""); setRole("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro a adicionar."); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remover o acesso de ${entry.email}? As sessões ativas desta pessoa serão terminadas.`)) return;
    try {
      await axios.delete(`${API}/admin/access/${entry.id}`, { withCredentials: true });
      toast.success("Acesso removido.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro a remover."); }
  };

  const saveRole = async (entry) => {
    try {
      await axios.patch(`${API}/admin/access/${entry.id}`, { role_label: editRole || null }, { withCredentials: true });
      setEditingId(null);
      load();
    } catch { toast.error("Erro a guardar."); }
  };

  const Row = ({ entry, protectedRow }) => (
    <li className="bg-white border border-[hsl(var(--border))] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`access-row-${entry.email}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <UserCircle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <span className="text-sm font-medium">{entry.email}</span>
          {protectedRow ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] px-1.5 py-0.5">
              <ShieldCheck className="h-3 w-3 text-[hsl(var(--accent))]" /> Fundador · protegido
            </span>
          ) : editingId === entry.id ? (
            <span className="inline-flex items-center gap-1.5">
              <input value={editRole} onChange={(e) => setEditRole(e.target.value)} placeholder="Função (ex.: Tesoureiro)" className={`${inputCls} py-1 text-xs w-44`} data-testid="access-edit-role" />
              <button onClick={() => saveRole(entry)} className="text-emerald-700" data-testid="access-save-role"><Check className="h-4 w-4" /></button>
              <button onClick={() => setEditingId(null)} className="text-[hsl(var(--muted-foreground))]"><X className="h-4 w-4" /></button>
            </span>
          ) : (
            <button onClick={() => { setEditingId(entry.id); setEditRole(entry.role_label || ""); }}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[hsl(var(--muted))] px-1.5 py-0.5 hover:bg-[hsl(var(--secondary))]"
              title="Editar função" data-testid={`access-role-${entry.email}`}>
              {entry.role_label || "sem função"} <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
          Último login: {fmtDT(entry.last_login_at)}
          {!protectedRow && entry.added_by ? ` · adicionado por ${entry.added_by} em ${fmtDT(entry.added_at)}` : ""}
        </div>
      </div>
      {!protectedRow && (
        <button onClick={() => remove(entry)} className="text-[hsl(var(--muted-foreground))] hover:text-red-700 shrink-0" title="Remover acesso" data-testid={`access-remove-${entry.email}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </li>
  );

  return (
    <div className="mt-6 max-w-3xl space-y-6">
      <form onSubmit={add} className="bg-white border border-[hsl(var(--border))] p-5 flex flex-wrap items-end gap-3" data-testid="access-add-form">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Email Google *</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="tesoureiro@exemplo.pt" className={`${inputCls} w-full`} data-testid="access-email" />
        </div>
        <div className="min-w-[180px]">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Função (opcional)</label>
          <input value={role} onChange={(e) => setRole(e.target.value)}
            placeholder="ex.: Tesoureiro" className={`${inputCls} w-full`} data-testid="access-role-input" />
        </div>
        <button type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630]" data-testid="access-add-button">
          <Plus className="h-4 w-4" /> Dar acesso
        </button>
        <p className="w-full text-[11px] text-[hsl(var(--muted-foreground))]">
          A pessoa entra em <strong>/admin</strong> com o botão "Entrar com Google" usando este email. Sem passwords a gerir.
        </p>
      </form>

      {loading ? (
        <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
      ) : (
        <ul className="space-y-2" data-testid="access-list">
          {data.founders.map((f) => <Row key={f.email} entry={f} protectedRow />)}
          {data.entries.map((e) => <Row key={e.id} entry={e} />)}
          {!data.entries.length && (
            <li className="text-sm text-[hsl(var(--muted-foreground))] bg-white border border-dashed border-[hsl(var(--border))] p-6 text-center">
              Ainda sem administradores adicionais. Adicione o tesoureiro e a secretária acima.
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const ActivityTab = () => {
  const [items, setItems] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: "300" });
      if (email) params.set("email", email);
      const r = await axios.get(`${API}/admin/audit?${params}`, { withCredentials: true });
      setItems(r.data.items || []);
      setAdmins(r.data.admins || []);
    } catch { toast.error("Erro a carregar atividade."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [email, days]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-6 max-w-4xl space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <select value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="audit-filter-email">
          <option value="">Todos os administradores</option>
          {admins.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={inputCls} data-testid="audit-filter-days">
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
          <option value={365}>Último ano</option>
        </select>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{items.length} registo(s)</span>
      </div>

      {loading ? (
        <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
      ) : !items.length ? (
        <div className="bg-white border border-[hsl(var(--border))] p-10 text-center">
          <History className="h-8 w-8 text-[hsl(var(--muted-foreground))] mx-auto" />
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Sem atividade registada no período.</p>
        </div>
      ) : (
        <ul className="space-y-1" data-testid="audit-list">
          {items.map((it) => (
            <li key={it.id} className="bg-white border border-[hsl(var(--border))] px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap text-sm" data-testid={`audit-row-${it.id}`}>
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${it.method === "LOGIN" ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-[hsl(var(--muted))]"}`}>
                  {it.method === "LOGIN" ? "Login" : it.method}
                </span>
                <span className="font-medium">{it.action}</span>
                <span className="text-xs text-[hsl(var(--muted-foreground))] truncate">{it.name || it.email}</span>
              </div>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] shrink-0">{fmtDT(it.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function AdminAccess() {
  const [tab, setTab] = useState("admins");
  return (
    <div className="p-6 lg:p-10" data-testid="admin-access-page">
      <div className="overline">Acessos</div>
      <h1 className="display-heading text-3xl mt-1">Acessos e atividade.</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
        Dê acesso ao painel a membros da Direção (tesoureiro, secretária…) e acompanhe quem fez o quê.
        Os logins e todas as ações que alteram dados ficam registados; consultas não são registadas.
      </p>

      <div className="mt-8 flex gap-1 border-b border-[hsl(var(--border))]">
        {[["admins", "Administradores"], ["activity", "Atividade"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${
              tab === k ? "border-[hsl(var(--accent))] text-[hsl(var(--primary))]"
                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
            data-testid={`access-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "admins" ? <AdminsTab /> : <ActivityTab />}
    </div>
  );
}
